import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthState, useSignInWithGoogle } from "react-firebase-hooks/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import auth from "../firebase.init";
import Loading from "../components/Shared/Loading";
import { Cross, House } from "lucide-react";

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/popup-closed-by-user":
      return "Sign-in popup closed by user.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked. Please allow popups for this site.";
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled. Please try again.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/invalid-email":
      return "Invalid email address format.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/too-many-requests":
      return "Too many failed login attempts. Please try again later.";
    default:
      return "An error occurred during login. Please try again.";
  }
};

const Login = () => {
  const [signInWithGoogle, gUser, gLoading, gError] = useSignInWithGoogle(auth);
  const [apiError, setApiError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailPasswordError, setEmailPasswordError] = useState(null);
  const [emailPasswordLoading, setEmailPasswordLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [userMail] = useAuthState(auth);

  useEffect(() => {
    if (userMail) {
      navigate("/user/dashboard", { replace: true });
    }
  }, [userMail, navigate]);

  let from = location.state?.from?.pathname || "/user/dashboard";
  let signUpError;

  useEffect(() => {
    if (gError || apiError || emailPasswordError) {
      window.scrollTo(0, 0);
    }
  }, [gError, apiError, emailPasswordError]);

  // Create user in database after successful Firebase authentication
  const createUserInDb = async (name, email, photoURL) => {
    try {
      const response = await fetch(
        `https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: name,
            userEmail: email,
            profileURL: photoURL || null,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create user");
      }
      console.log("User created with credits:", data);
      return data;
    } catch (err) {
      console.error("Error creating user in database:", err);
      setApiError(err.message);
      return null;
    }
  };

  useEffect(() => {
    const handleUserCreation = async () => {
      if (gUser) {
        setIsProcessing(true);
        const { displayName, email, photoURL } = auth.currentUser;
        await createUserInDb(displayName, email, photoURL);
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    };
    handleUserCreation();
  }, [gUser, from, navigate]);

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setEmailPasswordLoading(true);
    setApiError(null);
    setEmailPasswordError(null);

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user) {
        setIsProcessing(true);
        const { displayName, email, photoURL } = result.user;
        await createUserInDb(displayName, email, photoURL);
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    } catch (error) {
      setEmailPasswordError(getErrorMessage(error.code));
      console.error("Email/password login error:", error);
    } finally {
      setEmailPasswordLoading(false);
    }
  };

  if (gLoading || isProcessing || emailPasswordLoading) {
    return <Loading />;
  }

  if (gError || apiError || emailPasswordError) {
    const errorCode = gError?.code;
    signUpError = (
      <div className="error-message">
        <i className="fas fa-exclamation-circle"></i>
        <p>{apiError || emailPasswordError || getErrorMessage(errorCode)}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login - Welcome Back</title>
        <meta
          name="description"
          content="Discover proven strategies and tools to instantly find high-quality SEO clients. Learn how to attract, pitch, and close deals faster to grow your digital marketing business."
        />
      </Helmet>
      <section
        className="modern-register-page body-bg"
       
      >
        <div className="register-container"  style={{ marginTop: "200px" }}>
          <div className="register-card">
            <div className="register-header">
              <div className="close-button" >
                <Link to="/" className="close-link">
                  <House size={18} />
                </Link>
              </div>
              <div className="logo-section"></div>
              <div className="welcome-section ">
                <h1>Welcome Back</h1>
                <p>Sign in to continue to your account</p>
              </div>
            </div>
            <div className="register-content">
              {signUpError}

              {/* Email/Password Form */}
              <form
                onSubmit={handleEmailPasswordLogin}
                className="email-password-form"
              >
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                  />
                </div>
                <div className="form-options">
                  <Link to="/reset-password" className="reset-password">
                    Forgot Password?
                  </Link>
                </div>
                <button
                  type="submit"
                  className="email-password-signin-btn"
                  disabled={emailPasswordLoading}
                >
                  {emailPasswordLoading ? "Signing In..." : "Sign In"}
                </button>
              </form>

              <div className="features-section">
                <h3>Your Account Benefits?</h3>
                <div className="features-grid">
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Personalized dashboard</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Save your Preferences</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Track your progress</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="register-footer">
              <p>
                Don't have an account?{" "}
                <Link to="/register" className="register-link">
                  Sign up for free
                </Link>
              </p>
              {/* <div className="terms-section">
                <p>By continuing, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></p>
              </div> */}
            </div>
          </div>
        </div>
        <style jsx>{`
          .modern-register-page {
            min-height: 100vh;
            background-image: url(/images/shapes/login-page-shape-1.png);
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            position: relative;
            overflow: hidden;
          }
          .modern-register-page::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(1px);
          }
          .register-container {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 500px;
          }
          .register-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          .register-header {
            padding: 40px 40px 30px;
            text-align: center;
            position: relative;
          }
          .close-button {
            position: absolute;
            top: 20px;
            right: 20px;
          }
          .close-link {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: rgba(0, 0, 0, 0.1);
            border-radius: 50%;
            color: #666;
            text-decoration: none;
            transition: all 0.3s ease;
          }
          .close-link:hover {
            background: rgba(0, 0, 0, 0.2);
            color: #333;
          }
          .logo-section {
            margin-bottom: 30px;
          }
          .logo {
            height: 60px;
            width: auto;
          }
          .welcome-section h1 {
            font-size: 28px;
            font-weight: 700;
            color: #333;
            margin-bottom: 10px;
          }
          .welcome-section p {
            font-size: 16px;
            color: #666;
            margin: 0;
          }
          .register-content {
            padding: 0 40px 30px;
          }
          .error-message {
            background: #fee;
            border: 1px solid #fcc;
            border-radius: 12px;
            padding: 15px;
            margin-bottom: 25px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .error-message i {
            color: #d32f2f;
            font-size: 18px;
          }
          .error-message p {
            color: #d32f2f;
            margin: 0;
            font-size: 14px;
          }

          /* Email/Password Form Styles */
          .email-password-form {
            margin-bottom: 25px;
          }
          .form-group {
            margin-bottom: 20px;
          }
          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
            font-size: 14px;
          }
          .form-group input {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid #e0e0e0;
            border-radius: 10px;
            font-size: 15px;
            transition: all 0.3s ease;
            background-color: #fff;
          }
          .form-group input:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }
          .form-options {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .remember-me {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .remember-me input {
            width: 16px;
            height: 16px;
            accent-color: #667eea;
          }
          .remember-me label {
            font-size: 14px;
            color: #666;
            cursor: pointer;
          }
          .reset-password {
            font-size: 14px;
            color: #667eea;
            text-decoration: none;
            font-weight: 500;
          }
          .reset-password:hover {
            text-decoration: underline;
          }
          .email-password-signin-btn {
            width: 100%;
            height: 56px;
            background: #667eea;
            border: none;
            border-radius: 12px;

            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .email-password-signin-btn:hover {
            background: #5a67d8;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
          .email-password-signin-btn:disabled {
            background: #a0aec0;
            cursor: not-allowed;
            transform: none;
          }

          .divider {
            display: flex;
            align-items: center;
            margin: 25px 0;
            color: #999;
            font-size: 14px;
          }
          .divider::before,
          .divider::after {
            content: "";
            flex: 1;
            height: 1px;
            background: #e0e0e0;
          }
          .divider span {
            padding: 0 15px;
          }

          .google-signin-section {
            margin-bottom: 30px;
          }
          .google-signin-btn {
            width: 100%;
            height: 56px;
            background: #fff;
            border: 2px solid #e0e0e0;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-size: 16px;
            font-weight: 500;
            color: #333;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }
          .google-signin-btn:hover {
            border-color: #4285f4;
            box-shadow: 0 4px 12px rgba(66, 133, 244, 0.2);
            transform: translateY(-2px);
          }
          .google-signin-btn:active {
            transform: translateY(0);
          }
          .google-signin-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }
          .google-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .features-section {
            text-align: center;
          }
          .features-section h3 {
            font-size: 20px;
            font-weight: 600;
            color: #333;
            margin-bottom: 20px;
          }
          .features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .feature-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px;
            background: rgba(102, 126, 234, 0.1);
            border-radius: 8px;
            font-size: 14px;
            color: #555;
          }
          .feature-item i {
            color: #667eea;
            font-size: 16px;
          }
          .register-footer {
            padding: 30px 40px;
            background: rgba(0, 0, 0, 0.02);
            text-align: center;
            border-top: 1px solid rgba(0, 0, 0, 0.05);
          }
          .register-footer p {
            margin: 0 0 15px;
            color: #666;
            font-size: 14px;
          }
          .register-link {
            color: #667eea;
            text-decoration: none;
            font-weight: 500;
          }
          .register-link:hover {
            text-decoration: underline;
          }
          .terms-section p {
            font-size: 12px;
            color: #999;
            margin: 0;
          }
          .terms-section a {
            color: #667eea;
            text-decoration: none;
          }
          .terms-section a:hover {
            text-decoration: underline;
          }
          @media (max-width: 768px) {
            .modern-register-page {
              padding: 10px;
            }
            .register-header {
              padding: 30px 30px 20px;
            }
            .register-content {
              padding: 0 30px 20px;
            }
            .register-footer {
              padding: 20px 30px;
            }
            .welcome-section h1 {
              font-size: 24px;
            }
            .features-grid {
              grid-template-columns: 1fr;
            }
            .feature-item {
              justify-content: center;
            }
          }
          @media (max-width: 480px) {
            .register-header {
              padding: 20px 20px 15px;
            }
            .register-content {
              padding: 0 20px 15px;
            }
            .register-footer {
              padding: 15px 20px;
            }
            .welcome-section h1 {
              font-size: 22px;
            }
            .welcome-section p {
              font-size: 14px;
            }
            .google-signin-btn {
              height: 50px;
              font-size: 14px;
            }
            .email-password-signin-btn {
              height: 50px;
              font-size: 15px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default Login;
